import { bottomLevel } from "building-generation/elements/level/levelTools";
import { createBaysFromPerimeters } from "building-generation/generationTools/generateFloorBaysTools";
import type { Building } from "building-types/building/buildingTypes";
import type {
  FloorAssemblyOverride,
  GeneratedFloorBoundaries,
  HorizontalFloorAssemblyRoomDerived,
} from "building-types/floors/floorAssemblyTypes";
import type { FloorSplitData } from "building-types/floors/floorSplitDataTypes";
import { emptyFloorSplitData } from "building-types/floors/floorSplitDataTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { EdgeModifier } from "building-types/modifiers/edgeModifierTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { Table, RWTable } from "common/data/table";
import {
  getFloorSplitData,
  isHorizontalFloorAssemblyFreeForm,
  isHorizontalFloorAssemblyRoomDerived,
  subtractExcluded,
} from "doc/elements/floor/floorAssembly";
import { isVoidFloorAssemblyOverride } from "doc/elements/floor/floorAssembly";
import { floorOpeningStableEdgeId, floorPerimeterStableEdgeId } from "doc/elements/floor/floorShapeId";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { buildEdgeModifiersByParentId, getEntityEdgeModifiersTable, polyLine2Modify } from "doc/utils/modifierUtils";
import { polyLine2Copy, polyLine2EdgeCount, polyLine2SetCW } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { snapOutlinesToRoomOutlines } from "../elements/buildingGraph/snapOutlineToRoomOutlines";
import {
  mirrorFloorEdgeIdsMulti,
  mirrorFloorOpening,
  mirrorFloorPerimeter,
  mirrorSplitData,
} from "../elements/floor/floorMirrorUtils";
import {
  buildOpeningEdgeIdsFromRooms,
  filterOverridesByEdgeIds,
  openingsToUse,
  roomOutlinesToUnionPerimetersWithProvenance,
} from "../generationTools/generateFloorSystemBoundaryTools";
import { calcLevelFloorSettings } from "../generationTools/generateLevelsTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";
import { roomRemoveFloorStructure } from "../utils/roomOpenToBelow";

function select({
  doc: { optionSets, instanceOptionSets, settings: settingsTable, levels: levelDefs, edgeModifiers },
  horizontalAssemblyFloorSystems: floorSystems,
  horizontalAssemblyFloors: floors,
  levels,
  rooms,
  optionChoices,
  settings,
  entityDisabledByGroup,
  mirror,
  mirrorTranslationX,
  modifiers,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula({ floors, floorSystems }, definitions, optionChoices);
  const levelsFloorSettings = calcLevelFloorSettings(settingsTable, levelDefs, settings);

  return {
    floorSystems,
    floors,
    levels,
    levelsFloorSettings,
    rooms,
    resolvedOptionChoices,
    entityDisabledByGroup,
    mirror,
    mirrorTranslationX,
    edgeModifiers,
    modifiers,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  resolvedOptionChoices: "shallow",
  levelsFloorSettings: "deep",
  rooms: "shallow",
  floors: "shallow",
  floorSystems: "shallow",
  edgeModifiers: "deep",
  modifiers: "shallow",
});

export function resolveRoomIdsForBuilding(
  floor: HorizontalFloorAssemblyRoomDerived,
  rooms: Table<Room>,
): readonly ForeignEntityId<"ROOM">[] {
  if (!floor.allRoomsOnLevel) {
    return floor.roomIds ?? [];
  }
  const roomsOnLevel = Object.values(rooms)
    .filter((room) => room.level?.id === floor.levelId)
    .map((room) => room.id)
    .sort((a, b) => a.localeCompare(b));
  return subtractExcluded(roomsOnLevel, floor.excludedRoomIds);
}

function generate(input: ReturnType<typeof select>): Pick<Building, "floorBoundaries"> {
  const {
    floorSystems,
    floors,
    levels,
    rooms,
    resolvedOptionChoices,
    entityDisabledByGroup,
    mirror,
    mirrorTranslationX,
    edgeModifiers,
    modifiers,
  } = input;

  const edgeModifiersByParentId = buildEdgeModifiersByParentId(edgeModifiers);
  const floorBoundaries: RWTable<GeneratedFloorBoundaries> = {};
  const firstLevel = bottomLevel(levels);

  if (!firstLevel || !floorSystems || !floors) {
    return { floorBoundaries };
  }

  const resolvedFloorSystems = evalFormulas(
    floorSystems,
    (v) => resolvedOptionChoices[v],
    undefined,
    entityDisabledByGroup,
  );
  const resolvedFloors = evalFormulas(floors, (v) => resolvedOptionChoices[v], undefined, entityDisabledByGroup);

  for (const floorId in resolvedFloors) {
    const floor = resolvedFloors[floorId];
    const floorSystemId = floor.parentId;

    if (!floor || floor.disabled) {
      continue;
    }
    if (resolvedFloorSystems[floorSystemId]?.disabled) {
      continue;
    }
    if (isVoidFloorAssemblyOverride(floor)) {
      continue;
    }

    if (isHorizontalFloorAssemblyFreeForm(floor)) {
      const docPerimeters = floor.perimeters ?? [];
      if (docPerimeters.length === 0) {
        continue;
      }
      const floorEdgeModifiers = getEntityEdgeModifiersTable(edgeModifiersByParentId, floorId);
      const docPerimeterEdgeCounts = docPerimeters.map(polyLine2EdgeCount);
      const modifiedPerimeters = applyEdgeModifiersToSlices(
        "outline",
        docPerimeters,
        floor.edgeIds ?? [],
        docPerimeterEdgeCounts,
        floorEdgeModifiers,
        modifiers,
      );
      const docOpenings = floor.openings ?? [];
      const docOpeningEdgeCounts = docOpenings.map(polyLine2EdgeCount);
      const modifiedOpenings = applyEdgeModifiersToSlices(
        "opening",
        docOpenings,
        floor.openingEdgeIds ?? [],
        docOpeningEdgeCounts,
        floorEdgeModifiers,
        modifiers,
      );

      const perimeters: PolyLine2[] = modifiedPerimeters.map((p) =>
        mirror ? mirrorFloorPerimeter(polyLine2Copy(p), mirrorTranslationX) : polyLine2Copy(p),
      );
      const openings = openingsToUse(modifiedOpenings, mirror, mirrorTranslationX);
      // Edge counts are derived from polyline geometry, not stored on the doc.
      // Mirroring preserves edge count, so these match both original and mirrored polylines.
      const perimeterEdgeCounts = perimeters.map(polyLine2EdgeCount);
      const openingEdgeCounts = openings.map(polyLine2EdgeCount);
      // Free-form edgeIds are stable and stored in the doc; mirror each polyline's IDs independently.
      // The reordered edgeIds array maps geometry positions to the correct stable IDs.
      const edgeIds =
        mirror && floor.edgeIds ? mirrorFloorEdgeIdsMulti(floor.edgeIds, perimeterEdgeCounts) : floor.edgeIds;
      // Free-form openingEdgeIds: mirror each opening's IDs independently.
      const openingEdgeIds =
        mirror && floor.openingEdgeIds
          ? mirrorFloorEdgeIdsMulti(floor.openingEdgeIds, openingEdgeCounts)
          : floor.openingEdgeIds;
      const freeFormBoundary: GeneratedFloorBoundaries = {
        floorEdgeOverrides: filterOverridesByEdgeIds(floor.floorEdgeOverrides, edgeIds) ?? {},
        floorOpeningEdgeOverrides: filterOverridesByEdgeIds(floor.floorOpeningEdgeOverrides, openingEdgeIds) ?? {},
        perimeters,
        openings,
        edgeIds: edgeIds ?? [],
        perimeterEdgeCounts,
        openingEdgeIds: openingEdgeIds ?? [],
        openingEdgeCounts,
        splitData: mirror
          ? mirrorSplitData(floor.splitData ?? emptyFloorSplitData, mirrorTranslationX)
          : floor.splitData ?? emptyFloorSplitData,
      };
      floorBoundaries[floorId] = freeFormBoundary;
      ensureBaysForBoundaries(
        floorBoundaries,
        freeFormBoundary,
        freeFormBoundary.splitData,
        floorId,
        mirror,
        mirrorTranslationX,
      );
    } else if (isHorizontalFloorAssemblyRoomDerived(floor)) {
      const floorRoomIds = resolveRoomIdsForBuilding(floor, rooms);
      const roomOutlines: PolyLine2[] = [];
      const roomOutlineRoomIds: string[] = [];
      const openingRoomOutlines: PolyLine2[] = [];
      const openingRoomIds: string[] = [];
      for (const roomId of floorRoomIds) {
        const room: Room | undefined = rooms[roomId];
        if (room?.outline && room.outline.length >= 6) {
          if (roomRemoveFloorStructure(room)) {
            openingRoomOutlines.push(room.outline);
            openingRoomIds.push(roomId);
          } else {
            roomOutlines.push(room.outline);
            roomOutlineRoomIds.push(roomId);
          }
        }
      }

      const { perimeters: unionPerimeters, perimeterProvenance } =
        roomOutlinesToUnionPerimetersWithProvenance(roomOutlines);
      if (unionPerimeters.length === 0) {
        continue;
      }

      // Re-anchor room-derived perimeters to source room outlines so DCEL snap-round
      // drift (~4e-4") does not create micro-insets vs rooms. Freeform floors skip this.
      const perimeters = snapOutlinesToRoomOutlines(unionPerimeters, roomOutlines);

      // Build composite roomId:edgeId keys from DCEL provenance.
      // Each boundary edge traces back to a source room and edge index; the
      // room's own edgeIds array provides the stable string identifier.
      const edgeIds: string[] = [];
      const perimeterEdgeCounts: number[] = [];
      for (let pi = 0; pi < perimeters.length; pi++) {
        const prov = perimeterProvenance[pi];
        // Keep counts tied to provenance (and edgeIds), not the post-snap polyline.
        // snapOutlineToRoomOutlines may merge coincident verts, shrinking edge count.
        const count = prov.length;
        perimeterEdgeCounts.push(count);

        for (const p of prov) {
          const sourceRoomId = roomOutlineRoomIds[p.sourcePolygonIndex];
          const sourceRoom = rooms[sourceRoomId];
          const roomEdgeId = sourceRoom?.edgeIds?.[p.sourceEdgeIndex] ?? String(p.sourceEdgeIndex);
          edgeIds.push(`${sourceRoomId}:${roomEdgeId}`);
        }
      }

      const openings = openingRoomOutlines.map((outline) =>
        mirror
          ? mirrorFloorOpening(polyLine2SetCW(polyLine2Copy(outline)), mirrorTranslationX)
          : polyLine2SetCW(polyLine2Copy(outline)),
      );
      let openingResult =
        openings.length > 0 ? buildOpeningEdgeIdsFromRooms(openings, openingRoomIds, rooms) : undefined;
      if (mirror && openingResult) {
        openingResult = {
          ...openingResult,
          edgeIds: mirrorFloorEdgeIdsMulti(openingResult.edgeIds, openingResult.edgeCounts),
        };
      }

      const roomDerivedBoundary: GeneratedFloorBoundaries = {
        floorEdgeOverrides: filterOverridesByEdgeIds(floor.floorEdgeOverrides, edgeIds) ?? {},
        floorOpeningEdgeOverrides:
          filterOverridesByEdgeIds(floor.floorOpeningEdgeOverrides, openingResult?.edgeIds) ?? {},
        perimeters,
        openings,
        edgeIds,
        perimeterEdgeCounts,
        openingEdgeIds: openingResult?.edgeIds ?? [],
        openingEdgeCounts: openingResult?.edgeCounts ?? [],
        splitData: mirror
          ? mirrorSplitData(
              getFloorSplitData(floor as FloorAssemblyOverride) ?? emptyFloorSplitData,
              mirrorTranslationX,
            )
          : getFloorSplitData(floor as FloorAssemblyOverride) ?? emptyFloorSplitData,
      };
      floorBoundaries[floorId] = roomDerivedBoundary;
      ensureBaysForBoundaries(
        floorBoundaries,
        roomDerivedBoundary,
        roomDerivedBoundary.splitData,
        floorId,
        mirror,
        mirrorTranslationX,
      );
    }
  }
  return { floorBoundaries };
}

/**
 * Applies stored edge modifiers to each polyline in `polyLines` independently, slicing the
 * combined `edgeIds` array by `edgeCounts` (same slicing convention as {@link mirrorFloorEdgeIdsMulti}).
 * Modifiers are applied to the doc-space geometry before mirroring, matching room ordering.
 *
 * Perimeter and opening edge IDs are independently-numbered namespaces on a floor, so a bare
 * edge ID (e.g. `"2"`) can collide between a perimeter edge and an unrelated opening edge (or
 * even between two different perimeters). `EdgeModifier.edgeId` for a FLOOR parent is namespaced
 * by ring + index (see {@link floorPerimeterStableEdgeId}/{@link floorOpeningStableEdgeId}), so
 * each slice's raw IDs must be namespaced the same way here before matching against it.
 */
function applyEdgeModifiersToSlices(
  ring: "outline" | "opening",
  polyLines: PolyLine2[],
  edgeIds: string[],
  edgeCounts: number[],
  edgeModifiers: Table<EdgeModifier>,
  modifiers: Building["modifiers"],
): PolyLine2[] {
  let offset = 0;
  return polyLines.map((polyLine, index) => {
    const count = edgeCounts[index] ?? 0;
    const edgeIdsSlice = edgeIds
      .slice(offset, offset + count)
      .map((edgeId) =>
        ring === "outline" ? floorPerimeterStableEdgeId(index, edgeId) : floorOpeningStableEdgeId(index, edgeId),
      );
    offset += count;
    return polyLine2Modify(polyLine, edgeModifiers, modifiers, edgeIdsSlice);
  });
}

/**
 * Always generates bay boundaries from the parent boundary and adds them
 * to the floorBoundaries table keyed by deterministic bay IDs.
 * When split lines exist, delegates to generateFloorBays for polygon splitting.
 * Otherwise, creates one bay per perimeter.
 */
function ensureBaysForBoundaries(
  floorBoundaries: RWTable<GeneratedFloorBoundaries>,
  parentBoundary: GeneratedFloorBoundaries,
  splitData: FloorSplitData | undefined,
  floorId: string,
  mirror: boolean,
  mirrorTranslationX: number,
): void {
  const bayBoundaries = createBaysFromPerimeters(parentBoundary, splitData, floorId, mirror, mirrorTranslationX);
  for (const { bayId, boundary } of bayBoundaries) {
    floorBoundaries[bayId] = boundary;
  }
}

export default memo(select, generate, compare);
